import { Controller } from "@hotwired/stimulus"
import { Spring, prefersReducedMotion } from "../lib/spring"

// The name field doubles as the search: travellers the account has issued to
// before appear in a listbox under it as the operator types. Arrow keys move
// through them without leaving the field, Enter picks one — filling the
// email and linking the traveller, the step still there to check and move on
// from with Next — and Escape closes. A new name simply gets no list.
//
// The list grows out of the field's bottom edge on a spring and shrinks back
// the same way, so it can be dismissed while still opening.
export default class extends Controller {
  static targets = ["name", "email", "travellerId", "results"]
  static values = { url: String }

  static DEBOUNCE = 150 // ms after the last keystroke
  static PATIENCE = 200 // ms before admitting to searching

  connect() {
    this.timer = null
    this.request = null
    this.active = -1
    this.pickedEmail = null
    this.filling = false

    this.resultsTarget.style.transformOrigin = "top center"
    this.resultsTarget.style.display = "none"
    this.nameTarget.setAttribute("aria-expanded", "false")
    this.progress = new Spring({
      value: 0,
      response: 0.28,
      damping: 1,
      onUpdate: (value) => this.draw(value),
      onRest: (value) => { if (value === 0) this.emptyResults() }
    })

    document.addEventListener("pointerdown", this.closeOnPointerOutside)
  }

  disconnect() {
    clearTimeout(this.timer)
    this.request?.abort()
    this.progress.stop()
    document.removeEventListener("pointerdown", this.closeOnPointerOutside)
  }

  // --- Searching ---------------------------------------------------------

  search() {
    if (this.filling) return // a pick's own input event, not a keystroke
    clearTimeout(this.timer)
    const query = this.nameTarget.value.trim()
    if (query.length < 2) return this.close()
    this.timer = setTimeout(() => this.fetch(query), this.constructor.DEBOUNCE)
  }

  async fetch(query) {
    this.request?.abort()
    const request = this.request = new AbortController()
    const patience = setTimeout(() => this.render({ searching: true, query }), this.constructor.PATIENCE)

    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
        headers: { Accept: "application/json" },
        signal: request.signal
      })
      const travellers = await response.json()
      clearTimeout(patience)
      if (request !== this.request) return
      this.render({ travellers, query })
    } catch (error) {
      clearTimeout(patience)
      if (error.name !== "AbortError") this.close()
    }
  }

  render({ travellers = [], query, searching = false }) {
    let html
    if (searching) {
      html = `<div class="px-4 py-3 text-sm text-theme opacity-60">Searching…</div>`
    } else if (travellers.length === 0) {
      html = `<div class="px-4 py-3 text-sm text-theme opacity-60">No traveller matching “${this.escape(query)}” yet — a new one is saved when you issue.</div>`
    } else {
      html = travellers.map((traveller, index) =>
        `<button type="button" role="option" id="traveller-option-${index}" tabindex="-1"
          class="lookup-option w-full text-left px-4 py-3 text-sm cursor-pointer border-b border-black/5 last:border-0"
          data-action="traveller-lookup#select"
          data-traveller-id="${traveller.id}"
          data-traveller-name="${this.escape(traveller.full_name)}"
          data-traveller-email="${this.escape(traveller.email)}">
          <span class="font-medium">${this.escape(traveller.full_name)}</span>
          <span class="opacity-60 ml-1">${this.escape(traveller.email)}</span>
        </button>`
      ).join("")
    }

    this.resultsTarget.innerHTML = html
    this.activate(-1)
    this.open()
  }

  // --- Keyboard -----------------------------------------------------------

  navigate(event) {
    if (!this.isOpen) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.activate(this.active + 1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.activate(this.active - 1)
        break
      case "Enter":
        if (this.active >= 0) {
          // Ours, not the wizard's: picking answers the step by itself.
          event.preventDefault()
          event.stopPropagation()
          this.select({ currentTarget: this.options[this.active] })
        } else {
          this.close()
        }
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
    }
  }

  get options() {
    return [ ...this.resultsTarget.querySelectorAll("[role='option']") ]
  }

  get isOpen() {
    return this.nameTarget.getAttribute("aria-expanded") === "true"
  }

  activate(index) {
    const options = this.options
    this.active = options.length === 0 ? -1 : (index + options.length) % options.length
    options.forEach((option, i) => option.toggleAttribute("data-active", i === this.active))
    const current = options[this.active]
    if (current) {
      this.nameTarget.setAttribute("aria-activedescendant", current.id)
      current.scrollIntoView({ block: "nearest" })
    } else {
      this.nameTarget.removeAttribute("aria-activedescendant")
    }
  }

  // --- Picking ----------------------------------------------------------------

  select(event) {
    const option = event.currentTarget
    this.nameTarget.value = option.dataset.travellerName
    this.emailTarget.value = option.dataset.travellerEmail
    this.travellerIdTarget.value = option.dataset.travellerId
    this.pickedEmail = option.dataset.travellerEmail
    this.close({ instantly: true })
    // A pick is typing by other means: the wizard's counter and ticks listen
    // for input, so say so — without the field searching for what it just got.
    this.filling = true
    for (const field of [ this.nameTarget, this.emailTarget ]) {
      field.dispatchEvent(new Event("input", { bubbles: true }))
    }
    this.filling = false
    // Picking fills the step in but never leaves it: the operator reads the
    // address back and moves on with Next.
    this.nameTarget.focus()
  }

  // A different address after a pick is a different traveller: the server
  // finds or creates one by email, so the link is dropped.
  emailChanged() {
    if (this.travellerIdTarget.value && this.emailTarget.value.trim() !== this.pickedEmail) {
      this.travellerIdTarget.value = ""
      this.pickedEmail = null
    }
  }

  // --- Showing and hiding -------------------------------------------------

  open() {
    this.resultsTarget.style.display = ""
    this.nameTarget.setAttribute("aria-expanded", "true")
    if (prefersReducedMotion()) {
      this.progress.value = this.progress.target = 1
      this.draw(1)
      return
    }
    this.progress.to(1)
  }

  close({ instantly = false } = {}) {
    clearTimeout(this.timer)
    this.request?.abort()
    if (!this.isOpen && this.resultsTarget.style.display === "none") return
    this.nameTarget.setAttribute("aria-expanded", "false")
    this.activate(-1)
    if (instantly || prefersReducedMotion()) {
      this.progress.stop()
      this.progress.value = this.progress.target = 0
      this.emptyResults()
      return
    }
    this.progress.to(0)
  }

  emptyResults() {
    this.resultsTarget.style.display = "none"
    this.resultsTarget.innerHTML = ""
  }

  draw(value) {
    this.resultsTarget.style.transform = `scale(${(0.96 + 0.04 * value).toFixed(4)})`
    this.resultsTarget.style.opacity = String(Math.max(0, Math.min(1, value)))
  }

  closeOnPointerOutside = (event) => {
    if (this.isOpen && !this.element.contains(event.target)) this.close()
  }

  escape(text) {
    const div = document.createElement("div")
    div.textContent = text ?? ""
    return div.innerHTML
  }
}
